'use client'

import {
  Menu,
  MenuButton,
  MenuItems,
  type MenuItemsProps,
} from '@headlessui/react'
import { type MouseEvent, type PropsWithChildren, useMemo } from 'react'

import { cn } from '@mntn-dev/ui-utilities'
import { AnimatePresence, motion } from 'motion/react'
import { popoutVariants } from '../../../utils/popout.ts'
import { type TestIds, getChildTestProps } from '../../../utils/testing.ts'
import { Button, type ButtonProps } from '../../buttons/button.tsx'
import { PopoutItem } from './popout-item.tsx'

export type MenuPopoutOptions = Pick<MenuItemsProps, 'anchor' | 'className'>

type PopoutMenuProps = PropsWithChildren<
  { buttonProps?: ButtonProps; menuOptions?: MenuPopoutOptions } & TestIds
>

const defaultButtonProps: ButtonProps = {
  variant: 'text',
  size: 'sm',
  iconLeft: 'EllipsisVerticalIcon',
}

const defaultAnchorProps: MenuItemsProps['anchor'] = {
  to: 'bottom start',
  gap: '16px',
  padding: '32px',
}

const PopoutMenu = ({
  buttonProps,
  children,
  dataTestId,
  dataTrackingId,
  menuOptions,
}: PopoutMenuProps) => {
  const mergedButtonProps: ButtonProps = useMemo(
    () => ({
      ...(buttonProps ?? defaultButtonProps),
      onClick: (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        buttonProps?.onClick?.(e)
      },
      dataTestId,
      dataTrackingId,
    }),
    [buttonProps, dataTestId, dataTrackingId]
  )

  return (
    <Menu>
      {({ open }) => (
        <>
          <MenuButton as={Button} {...mergedButtonProps} />
          <AnimatePresence>
            {open && (
              <MenuItems
                static
                as={motion.div}
                initial="init"
                animate="open"
                exit="close"
                variants={popoutVariants}
                anchor={menuOptions?.anchor ?? defaultAnchorProps}
                className={cn('popover-panel', menuOptions?.className)}
                {...getChildTestProps(
                  { dataTestId, dataTrackingId },
                  'menu-panel'
                )}
              >
                {children}
              </MenuItems>
            )}
          </AnimatePresence>
        </>
      )}
    </Menu>
  )
}

const PopoutMenuComponent = Object.assign(PopoutMenu, {
  Item: PopoutItem,
})

export { PopoutMenuComponent as PopoutMenu }
